import { useEffect } from 'react'
import { GoogleTagManager } from '@next/third-parties/google'
import { Analytics } from '@vercel/analytics/react'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { ThemeProvider } from 'next-themes'
import { GTM_ID } from '@/utils/gtag'
import { loadMixpanel, trackMxPageView } from '@/utils/mixpanel'
import Providers from '@/contexts/providers'
import useDeferredMount from '@/hooks/useDeferredMount'
import DefaultSeo from '@/components/seo/default'
import type { AppProps } from 'next/app'
import '@/styles/app.css'
import '@/styles/app.scss'
import Layout from '@/views/layout'

function StudioApp({ Component, pageProps }: AppProps) {
  const router = useRouter()
  // GTM（約 340KB）は afterInteractive 固定でロードされ TBT を押し上げるため、
  // load 後のアイドル時、または最初のユーザー操作まで待ってからマウントする。
  // sendGTMEvent はスクリプト読み込み前でも window.dataLayer にキューするので、
  // 遅延させてもイベントは失われない。
  const isAnalyticsReady = useDeferredMount()

  // mixpanel（約 78KB gz）も GTM と同じタイミングまで遅延させる。
  // それまでの trackMx* 呼び出しは utils/mixpanel 側のキューに積まれ、
  // 初期化直後に順番どおり送信される。
  useEffect(() => {
    if (!isAnalyticsReady) {
      return
    }
    loadMixpanel()
  }, [ isAnalyticsReady ])

  useEffect(() => {
    // Track page views on route change
    const handleRouteChange = () => {
      trackMxPageView()
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [ router.events ])

  useEffect(() => {
    trackMxPageView()
  }, [])

  return (
    <>
      <DefaultSeo />
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        forcedTheme={pageProps.theme}
      >
        <Providers>
          <Layout
            hideLogin={pageProps.hideLogin}
            hideLayout={pageProps.hideLayout}
          >
            <Component {...pageProps} />
            {isAnalyticsReady && <GoogleTagManager gtmId={GTM_ID} />}
          </Layout>
        </Providers>
      </ThemeProvider>
      <Analytics />
      {/* HubSpot のチャットは約 765KB とサードパーティ中で最も重く、初期表示には不要。
          lazyOnload で load イベント後のアイドル時まで読み込みを遅らせる。 */}
      <Script
        id="hs-script-loader"
        src="https://js-na2.hs-scripts.com/245938472.js"
        strategy="lazyOnload"
      />
    </>
  )
}

export default StudioApp
